import { useParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Skeleton, Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useIamGroup,
  useGroupPolicyMutation,
  useGroupMemberMutation,
  s3ExtractError,
} from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../../S3ConsoleBreadcrumbs';
import { PolicyAttachments } from '../PolicyAttachments';
import { GroupMembers } from './GroupMembers';

/** IAM group detail — members (add/remove) and attached policies (attach/detach). */
export function GroupDetail({ serviceId }: { serviceId: string }) {
  const toast = useToast();
  const { name = '' } = useParams<{ name: string }>();
  const groupName = decodeURIComponent(name);
  const { data, isLoading } = useIamGroup(serviceId, groupName);
  const attach = useGroupPolicyMutation(serviceId, true);
  const detach = useGroupPolicyMutation(serviceId, false);
  const addUser = useGroupMemberMutation(serviceId, true);
  const removeUser = useGroupMemberMutation(serviceId, false);

  const runPolicy = (m: typeof attach, policyArn: string, ok: string, fail: string) =>
    m.mutate(
      { name: groupName, policyArn },
      { onSuccess: () => toast.success(ok), onError: (e) => toast.error(s3ExtractError(e, fail)) },
    );

  const runMember = (m: typeof addUser, userName: string, ok: string, fail: string) =>
    m.mutate(
      { name: groupName, userName },
      { onSuccess: () => toast.success(ok), onError: (e) => toast.error(s3ExtractError(e, fail)) },
    );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <S3ConsoleBreadcrumbs trail={[{ label: 'Groups', slug: 'iam/groups' }, { label: groupName }]} />
        }
        title={groupName}
        description="IAM group members and attached policies."
      />

      {isLoading ? (
        <Skeleton variant="rect" height={320} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>Members</CardTitle>
            </CardHeader>
            <CardBody>
              <GroupMembers
                serviceId={serviceId}
                members={data?.members ?? []}
                pending={addUser.isPending || removeUser.isPending}
                onAdd={(u) => runMember(addUser, u, 'User added', 'Failed to add user')}
                onRemove={(u) => runMember(removeUser, u, 'User removed', 'Failed to remove user')}
              />
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Attached policies</CardTitle>
            </CardHeader>
            <CardBody>
              <PolicyAttachments
                serviceId={serviceId}
                attached={data?.attached_policies ?? []}
                pending={attach.isPending || detach.isPending}
                onAttach={(arn) => runPolicy(attach, arn, 'Policy attached', 'Failed to attach policy')}
                onDetach={(arn) => runPolicy(detach, arn, 'Policy detached', 'Failed to detach policy')}
              />
            </CardBody>
          </Card>
        </div>
      )}
    </Container>
  );
}
